import { DateTime, Interval } from 'luxon';

export const longDateFormat = (date: string) =>
  DateTime.fromISO(date).toFormat('LLLL yyyy');

export const fullLongDateFormat = (date: string) =>
  DateTime.fromISO(date).toFormat('dd LLLL yyyy');

export const shortDateFormat = (date: string) =>
  DateTime.fromISO(date).toFormat('yyyy');

export const completeDateFormat = (date: string | DateTime) => {
  const formattedDate = (
    date instanceof DateTime ? date : DateTime.fromISO(date)
  ).toFormat('MMM d, yyyy');

  return formattedDate === 'Invalid DateTime' ? '' : formattedDate;
};

export const dateFormat = (date: string) =>
  DateTime.fromISO(date).toFormat('D');

export const dateFormatWithLeadingZeroForMonth = (date: string) =>
  DateTime.fromISO(date).toFormat('MM/dd/yyyy');

export const completeFullDateFormat = (
  date: string,
  dateFormatType?: string
) => {
  const formattedDate = DateTime.fromISO(date).toFormat(
    dateFormatType ?? 'MMMM d, yyyy'
  );
  return formattedDate === 'Invalid DateTime' ? '' : formattedDate;
};

export const calculatePeriod = (startDate: string, endDate?: string) =>
  Interval.fromDateTimes(
    DateTime.fromISO(startDate),
    DateTime.fromISO(endDate || DateTime.now().toString())
  )
    .toDuration(['years', 'months', 'days'])
    .toObject();

export const getDurationInterval = (props?: {
  startDate?: string;
  endDate?: string;
}) =>
  Interval.fromDateTimes(
    DateTime.fromISO(props?.startDate || DateTime.now().toString()),
    DateTime.fromISO(props?.endDate || DateTime.now().toString())
  )
    .toDuration(['years', 'months', 'days'])
    .toObject();

export const getDataInISO = (date: string) =>
  DateTime.fromFormat(date, 'MMM dd, yyyy')?.toISO({ format: 'extended' });

export const monthYearToDateString = (month?: number, year?: number) =>
  month && year ? DateTime.fromObject({ year, month }).toISO() : '';

export const dateStringToMonthYear = (date?: string) =>
  date
    ? {
        month: DateTime.fromJSDate(new Date(date)).get('month'),
        year: DateTime.fromJSDate(new Date(date)).get('year'),
      }
    : null;

export const longMonthFormat = (date: string) => {
  return DateTime.fromISO(date).monthLong;
};

export const longYearFormat = (date: string) => {
  return DateTime.fromISO(date).year;
};

export const getDate = (date: string) => {
  return DateTime.fromISO(date).day;
};

export const calculatePeriodInString = (start: string, end: string) => {
  const startDate = getDate(start);
  const endDate = getDate(end);
  const startMonth = longMonthFormat(start);
  const endMonth = longMonthFormat(end);
  const startYear = longYearFormat(start);
  const endYear = longYearFormat(end);
  const isSameDate = startDate === endDate;
  const isSameMonth = startMonth === endMonth;
  const isSameYear = startYear === endYear;

  if (isSameYear) {
    if (isSameDate && isSameMonth) {
      return `${startMonth} ${startDate}, ${startYear}`;
    } else if (isSameMonth) {
      return `${startMonth} ${startDate}-${endDate}, ${startYear}`;
    } else {
      return `${startMonth} ${startDate} - ${endMonth} ${endDate}, ${startYear}`;
    }
  } else {
    return `${startMonth} ${startDate}, ${startYear} - ${endMonth} ${endDate}, ${endYear}`;
  }
};
